import {
  createNumberEntityRef,
  createThunk,
  model,
  Model,
  getReferenceParentOfType,
} from 'mst-collection';
import {
  applySnapshot,
  Instance,
  types,
  castToReferenceSnapshot,
  getRoot,
} from 'mobx-state-tree';

import { PostDataModel } from './PostDataModel';
import { UserModel } from '../Viewer/UserModel';
import { enumerationNumber } from '../types';
import { PostTypeEnum, TariffPlanCodeEnum } from '@constants';
import { ProcessedCommentListModel } from '../Comments/CommentListModel';
import { Api, IPostResponse, IUpdatePostParams } from '@api';
import { LikesUserListModel } from '@models/Viewer/LikesUserListModel';
import { PostListModel } from './PostListModel';
import { UserPostListModel } from './UserPostListModel';
import { trim } from '@utils';
import type { RootModelType } from '../RootModel';

export class Post extends Model({
  id: types.identifierNumber,
  type: enumerationNumber([
    PostTypeEnum.UserStatus,
    PostTypeEnum.VideoTutorial,
    PostTypeEnum.InviteFriend,
    PostTypeEnum.AudioTrack,
  ]),
  userId: types.maybeNull(types.number),
  itemData: PostDataModel,
  user: types.maybeNull(types.late(() => types.reference(UserModel))),
  comments: types.optional(ProcessedCommentListModel, {}),
  likes: types.optional(LikesUserListModel, {}),
}) {
  get canBeReported() {
    return !!this.userId;
  }

  get isAvailableForCurrentPlan(): boolean {
    return !(
      getRoot<RootModelType>(this).viewer.currentUser?.tariffPlan
        .id === TariffPlanCodeEnum.Trial &&
      !this.itemData.isForTrial &&
      this.type === PostTypeEnum.VideoTutorial
    );
  }

  sendReport = createThunk((reasonTypeIds: number[]) => {
    return async function sendReport(this: Post) {
      await Api.Feed.sendReport({
        id: this.id,
        reasonTypeIds,
      });
    };
  });

  update = createThunk((params: IUpdatePostParams) => {
    return async function create(this: Post) {
      let text;

      if (params.text) {
        text = trim(params.text, true);
      }

      await Api.Feed.updatePost({
        ...params,
        id: this.id,
        text,
      });

      const post = await Api.Feed.getPostById({
        id: this.id,
      });

      applySnapshot(this, {
        ...post.data,
        user: castToReferenceSnapshot(this.user),
      });
    };
  });

  remove = createThunk(() => {
    return async function remove(this: Post) {
      await Api.Feed.deletePost({
        id: this.id,
      });

      let parentList = getReferenceParentOfType(
        this,
        PostListModel,
      )?.find((parent) => parent.findById(this.id));

      if (!parentList) {
        // Post can be in several lists - PostList and UserPostList
        parentList = getReferenceParentOfType(
          this,
          UserPostListModel,
        )?.find((parent) => parent.findById(this.id));
      }

      parentList?.removeById(this.id);
    };
  });
}

// TODO: replace by not deprecated method
export const PostModel = model(Post).preProcessSnapshot(
  (snapshot: IPostResponse) => ({
    ...snapshot,
    comments: snapshot.itemData.comments,
  }),
);
// We set this name manually because after building the project into production
// mode code will be obfuscated and in the build js code class Post will get
// another name, class R for example. But we need this name to check parentEntity
// type (parentEntityName), for this case, we are using model names. The best way
// is to use parent.is(PostModel) but due to an import cycled dependency issue we
// cannot import PostModel or Post class into needed files, so we are using parent.name
// to detect the parent's model type (Post, Tutorial, or Track, etc)
// TODO: find a way to import Models in needed files
// (take a look at parentEntityName and parentEntity in CommentListModel and LikesUserList)
PostModel.name = 'Post';

export const PostRefModel = createNumberEntityRef('posts', PostModel);

export type PostRefModelType = Instance<typeof PostRefModel>;
